import React, { useCallback, useEffect, useRef, useState } from "react";
import { useStore } from "@/stores/useStore";
import { useToast } from "@/hooks/useToast";
import { useEmployeeId } from "@/hooks/useEmployeeId";
import { useInboxPanel } from "@/hooks/useInboxPanel";
import { useRoPanelParams } from "@/hooks/useRoPanelParams";
import { useTasksAnalytics } from "@/hooks/useTasksAnalytics";
import { useOverdueTaskToastAnalytics } from "@/hooks/useOverdueTaskToastAnalytics";
import { useToastSound } from "@/hooks/useToastSound";
import { getCardSection } from "@/lib/cards";
import {
  selectMyOverdueTasks,
  planOverdueToast,
  TaskStatus,
} from "@/lib/taskSelectors";
import { canClaimToastSlot, TOAST_PRIORITY } from "@/lib/toastEvictionCooldown";

const TICK_MS = 30_000;
const TASKS_TAB = "tasks";

export const MyOverdueTaskToastManager: React.FC = () => {
  const tasks = useStore((state) => state.tasks);
  const cards = useStore((state) => state.cards);
  const shop = useStore((state) => state.shop);
  const setTaskListFilterPreset = useStore(
    (state) => state.setTaskListFilterPreset
  );
  const { employeeId } = useEmployeeId();
  const { showTask, toasts } = useToast();
  const { isOpen: isInboxPanelOpen, openPanel } = useInboxPanel();
  const { setActiveTab } = useRoPanelParams();
  const tasksTabVisible = useStore((state) => state.isTasksTabVisible);
  const { playToastSound } = useToastSound();
  const { logTasksTabOpened } = useTasksAnalytics();
  const { logToastShown, logMarkCompleteClicked } =
    useOverdueTaskToastAnalytics();

  const [now, setNow] = useState<Date>(() => new Date());

  const notifiedIdsRef = useRef<Set<string>>(new Set());
  const currentToastRef = useRef<{ id: string; dismiss: () => void } | null>(
    null
  );
  const shownCountRef = useRef<number | null>(null);

  // Wall-clock tick: overdue is time-based, so re-evaluate on an interval even
  // when no Firestore write occurs.
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  const dismissActive = useCallback(() => {
    if (currentToastRef.current) {
      currentToastRef.current.dismiss();
      currentToastRef.current = null;
      shownCountRef.current = null;
    }
  }, []);

  // Dismiss on unmount (leaving the dashboard).
  useEffect(() => {
    return () => dismissActive();
  }, [dismissActive]);

  useEffect(() => {
    const isTasksTabVisible = isInboxPanelOpen && tasksTabVisible;
    // Suppress while the user is already looking at the Tasks tab.
    if (isTasksTabVisible) {
      dismissActive();
      return;
    }

    const myOverdue = selectMyOverdueTasks(tasks, employeeId, now);
    const plan = planOverdueToast(
      myOverdue,
      notifiedIdsRef.current,
      shownCountRef.current
    );

    if (plan.type === "none") return;

    if (plan.type === "dismiss") {
      dismissActive();
      return;
    }

    // plan.type === "render" from here. Check priority before mutating any
    // refs: if a higher-priority toast (customer update) holds the slot,
    // skip this cycle entirely and leave notifiedIdsRef/shownCountRef
    // untouched, so the exact same plan is recomputed and re-attempted on
    // the next TICK_MS tick rather than being marked handled while never
    // actually shown.
    if (
      !canClaimToastSlot({
        toasts,
        myPriority: TOAST_PRIORITY.OVERDUE_TASK,
        myToastId: currentToastRef.current?.id ?? null,
      })
    ) {
      return;
    }

    const isNewBatch = plan.notifyIds.some(
      (id) => !notifiedIdsRef.current.has(id)
    );
    plan.notifyIds.forEach((id) => notifiedIdsRef.current.add(id));

    // Play the sound only when this render surfaces genuinely new tasks.
    if (isNewBatch) playToastSound("task");

    dismissActive();

    const { count, task } = plan.model;
    const relatedCard = task?.relatedRO
      ? cards.find((c) => c.id === task.relatedRO)
      : undefined;
    const roLabel =
      relatedCard && shop
        ? getCardSection(relatedCard, shop, "primaryField")
        : undefined;

    logToastShown(count === 1 ? "single" : "digest");

    const toastInstance = showTask({
      count,
      taskTitle: task?.title,
      roLabel: roLabel || undefined,
      onComplete: task?.id
        ? async () => {
            logMarkCompleteClicked();
            try {
              await useStore.getState().completeTask(task.id as string);
            } finally {
              dismissActive();
            }
          }
        : undefined,
      onViewTasks: () => {
        setTaskListFilterPreset({
          statuses: [TaskStatus.OVERDUE],
          assigneeIds: employeeId ? [employeeId] : [],
          showCompleted: false,
        });
        // Matches useRoPanelParams.selectCard's convention (set the tab
        // before opening the panel), kept for consistency with that call
        // site. Note: this reorder alone did NOT fix WEBV3-270 (View
        // tasks/Open task landing on the wrong tab) -- that bug is still
        // open; the actual defect is elsewhere.
        setActiveTab(TASKS_TAB);
        openPanel();
        logTasksTabOpened("toast");
        dismissActive();
      },
      onDismiss: () => dismissActive(),
      // A higher-priority toast (customer update) preempted this one. Unlike
      // onDismiss, un-mark the keys we just notified so planOverdueToast
      // treats them as new again on the next tick -- otherwise this manager
      // would treat the eviction exactly like a user-initiated dismissal and
      // never re-offer the same overdue task this session, which is the bug
      // this ticket exists to fix.
      onEvicted: () => {
        plan.notifyIds.forEach((key) => notifiedIdsRef.current.delete(key));
        dismissActive();
      },
    });
    currentToastRef.current = {
      id: toastInstance.id,
      dismiss: toastInstance.dismiss,
    };
    shownCountRef.current = count;
  }, [
    tasks,
    employeeId,
    now,
    isInboxPanelOpen,
    tasksTabVisible,
    cards,
    shop,
    showTask,
    toasts,
    playToastSound,
    openPanel,
    setActiveTab,
    setTaskListFilterPreset,
    dismissActive,
    logTasksTabOpened,
    logToastShown,
    logMarkCompleteClicked,
  ]);

  return null;
};
